import { useCallback, useMemo, useState } from "react";

const normalizeFilters = (filters = {}) => {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (typeof value === "string") {
      acc[key] = value.trim();
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

const isEmptyFilterValue = (value) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim() === "";
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

const hasActiveValues = (filters = {}, ignoredKeys = []) => {
  return Object.entries(filters).some(([key, value]) => {
    if (ignoredKeys.includes(key)) {
      return false;
    }

    return !isEmptyFilterValue(value);
  });
};

function useExternalTableFilters(initialFilters = {}) {
  const [filters, setFilters] = useState(initialFilters);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const patchFilters = useCallback((patch = {}) => {
    setFilters((prev) => ({
      ...prev,
      ...patch,
    }));
  }, []);

  const resetFilters = useCallback((nextFilters = initialFilters) => {
    setFilters(nextFilters);
  }, [initialFilters]);

  const normalizedFilters = useMemo(() => normalizeFilters(filters), [filters]);

  const hasActiveFilters = useCallback(
    (ignoredKeys = []) => hasActiveValues(normalizedFilters, ignoredKeys),
    [normalizedFilters]
  );

  return {
    filters,
    normalizedFilters,
    setFilter,
    patchFilters,
    resetFilters,
    hasActiveFilters,
  };
}

export default useExternalTableFilters;
